import { AUTO_LOGIN_ROUTE, HOME_AUTH_LOGIN_ROUTE } from '../../routes/namedRoutes';

/**
 * Value of the `loginSource` query parameter on magic links emailed from the
 * login page. Only these token logins stay on /login and count as logins.
 */
export const MAGIC_LINK_LOGIN_SOURCE = 'magicLink';

/**
 * Where a token auto-login came from, as reported to analytics.
 * `integration` covers untagged URLs (backend/partner-built integration links).
 */
export type AutoLoginSource = 'integration' | 'registration' | 'serviceNation' | 'signupMagicLink';

const TAGGED_AUTO_LOGIN_SOURCES: readonly string[] = ['registration', 'serviceNation', 'signupMagicLink'];

/**
 * Maps the raw `loginSource` query parameter to a known auto-login source so
 * typos or unexpected values never reach analytics as free text.
 */
export const toAutoLoginSource = (loginSource: string | null): AutoLoginSource => (
  loginSource && TAGGED_AUTO_LOGIN_SOURCES.includes(loginSource) ?
    loginSource as AutoLoginSource :
    'integration'
);

/**
 * Returns the auto-login URL for a /login URL that carries an access token
 * from anywhere other than a login-page magic link, or null when the URL
 * should stay as it is.
 *
 * Integration URLs are built by the backend and by per-org URL templates, and
 * partners may build them too, so they can't all be moved off /login at the
 * source. Rewriting before Heap loads keeps those visits out of the /login
 * pageview.
 */
export const getAutoLoginRedirect = (pathname: string, search: string): string | null => {
  if (pathname.replace(/\/+$/, '') !== HOME_AUTH_LOGIN_ROUTE) {
    return null;
  }

  const params = new URLSearchParams(search);
  if (!params.get('accessToken') || params.get('loginSource') === MAGIC_LINK_LOGIN_SOURCE) {
    return null;
  }

  return `${AUTO_LOGIN_ROUTE}?${params.toString()}`;
};

/**
 * Applies getAutoLoginRedirect to the current page without a reload or a new
 * history entry. Must run before analytics (Heap) initialise.
 */
export const redirectTokenLoginOffLoginPage = () => {
  const { pathname, search, hash } = window.location;
  const redirect = getAutoLoginRedirect(pathname, search);
  if (redirect) {
    window.history.replaceState(window.history.state, '', `${redirect}${hash}`);
  }
};
